import dayjs from 'dayjs'
import type { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { ActiveFilter, AddFilterButton } from '#/shared/ui/active-filter'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import { useShipmentFilterOptions } from '../lib/use-shipment-filter-options'
import {
  SHIPMENT_FILTER_KEYS,
  SHIPMENT_MULTI_FILTER_KEYS,
  getDateFilterConfig,
  getMultiFilterConfig,
  getStageDateConfig,
} from '../lib/use-shipment-list-filters'
import type {
  ShipmentFilterKey,
  ShipmentListFiltersState,
  ShipmentMultiFilterKey,
} from '../lib/use-shipment-list-filters'

const CHIP_DATE_FORMAT = 'MMM D, YYYY'

// Beyond this the chip stops listing values and shows a "+N" overflow instead.
const CHIP_MAX_LABELS = 2

function formatRangeValue(range: DateRange): string {
  const from = range.from ? dayjs(range.from).format(CHIP_DATE_FORMAT) : ''
  const to = range.to ? dayjs(range.to).format(CHIP_DATE_FORMAT) : ''
  if (from && to) return `${from} – ${to}`
  return from !== '' ? from : to
}

interface ShipmentActiveFiltersProps {
  state: ShipmentListFiltersState
}

/**
 * Chip-row content for any shipment list page's toolbar — renders an
 * `ActiveFilter` per active date range and per active multi-value filter,
 * plus an `AddFilterButton` that reopens the FiltersButton popover owned by
 * the same state object.
 */
export function ShipmentActiveFilters({ state }: ShipmentActiveFiltersProps) {
  const { t } = useTranslation()
  return (
    <>
      <DateRangeChips state={state} />
      <MultiValueChips state={state} />
      <AddFilterButton onClick={() => state.setOpen(true)} />
      <button
        type="button"
        onClick={state.clearAll}
        className="flex h-8 cursor-pointer items-center rounded-md px-2 py-1 text-sm text-destructive hover:bg-gray-50"
      >
        {t('toolbar.clearAll')}
      </button>
    </>
  )
}

function DateRangeChips({ state }: ShipmentActiveFiltersProps) {
  const { t } = useTranslation()
  const stageDate = getStageDateConfig(state.stage)
  const entries = Object.entries(state.ranges).filter(
    (entry): entry is [ShipmentFilterKey, DateRange] => {
      const range = entry[1] as DateRange | undefined
      return Boolean(range?.from ?? range?.to)
    },
  )
  return (
    <>
      {entries.map(([key, range]) => {
        const labelKey =
          key === SHIPMENT_FILTER_KEYS.STAGE_DATE
            ? stageDate.labelI18nKey
            : getDateFilterConfig(key).labelI18nKey
        return (
          <ActiveFilter
            key={key}
            label={t(labelKey)}
            value={formatRangeValue(range)}
            onRemove={() => state.setRange(key, undefined)}
          />
        )
      })}
    </>
  )
}

function MultiValueChips({ state }: ShipmentActiveFiltersProps) {
  const { t } = useTranslation()
  const hasSourcesApplied = Boolean(
    state.multi[SHIPMENT_MULTI_FILTER_KEYS.SOURCES]?.length,
  )
  // Shares its query key with the popover's copy, so TanStack Query dedupes
  // this into the same single providers request.
  const options = useShipmentFilterOptions(state.isOpen || hasSourcesApplied)

  const optionsByKey: Record<ShipmentMultiFilterKey, MultiSelectOption[]> = {
    [SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE]: options.timezones,
    [SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE]: options.states,
    [SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE]: options.states,
    [SHIPMENT_MULTI_FILTER_KEYS.SOURCES]: options.sources,
  }

  const applied = state.multiKeys
    .map((key) => [key, state.multi[key] ?? []] as const)
    .filter(([, values]) => values.length > 0)

  // Falls back to the raw value (a state code, or a provider id while the
  // source list is still loading) so a chip never renders empty.
  const toLabel = (key: ShipmentMultiFilterKey, value: string): string =>
    optionsByKey[key].find((option) => option.value === value)?.label ?? value

  return (
    <>
      {applied.map(([key, values]) => {
        const labels = values.map((value) => toLabel(key, value))
        const shown = labels.slice(0, CHIP_MAX_LABELS).join(', ')
        const overflow = labels.length - CHIP_MAX_LABELS
        return (
          <ActiveFilter
            key={key}
            label={t(getMultiFilterConfig(key).labelI18nKey)}
            value={
              overflow > 0
                ? `${shown} ${t('toolbar.filterChipOverflow', { count: overflow })}`
                : shown
            }
            onRemove={() => state.setMulti(key, undefined)}
          />
        )
      })}
    </>
  )
}
